<script lang="ts">
  import Icon from "../Icon.svelte";
  import type { Workflow } from "../../lib/commands";

  interface Props {
    workflow: Workflow;
  }

  let { workflow }: Props = $props();

  // Mock workflow steps
  let steps = $derived.by(() => {
    if (workflow.id === "swap-uniswap") {
      return [
        { name: "Select tokens", description: "Choose input and output tokens", done: false },
        { name: "Enter amount", description: "Specify how much to swap", done: false },
        { name: "Review route", description: "Best rate via Uniswap V3", done: false },
        { name: "Execute swap", description: "Confirm transaction", done: false },
      ];
    }
    if (workflow.id === "bridge-base") {
      return [
        { name: "Select amount", description: "Enter ETH to bridge", done: false },
        { name: "Review bridge", description: "Via official Base bridge", done: false },
        { name: "Confirm", description: "Sign transaction", done: false },
      ];
    }
    return [
      { name: "Configure", description: "Set parameters", done: false },
      { name: "Execute", description: "Run workflow", done: false },
    ];
  });
</script>

<div class="workflow-result fade-in-up">
  <header class="result-header">
    <div class="result-icon">
      <Icon name={workflow.icon} size={20} />
    </div>
    <div class="result-title">
      <h2>{workflow.name}</h2>
      <span class="result-desc">{workflow.description}</span>
    </div>
    {#if workflow.source === "official"}
      <span class="source-badge official">
        <Icon name="shield" size={12} />
        Official
      </span>
    {:else if workflow.source === "protocol"}
      <span class="source-badge protocol">{workflow.protocol}</span>
    {/if}
  </header>

  <div class="workflow-steps">
    {#each steps as step, i}
      <div class="step" class:first={i === 0}>
        <div class="step-number">{i + 1}</div>
        <div class="step-content">
          <span class="step-name">{step.name}</span>
          <span class="step-desc">{step.description}</span>
        </div>
        {#if i === 0}
          <div class="step-active">
            <Icon name="chevron-right" size={16} />
          </div>
        {/if}
      </div>
    {/each}
  </div>

  <!-- Workflow UI placeholder -->
  <div class="workflow-ui">
    <div class="ui-placeholder">
      <Icon name="zap" size={32} />
      <p>Workflow interface would render here</p>
      <span>This is where the actual swap/bridge/etc UI would appear</span>
    </div>
  </div>

  <div class="actions">
    <button class="action-btn primary">
      <Icon name="zap" size={16} />
      Execute Workflow
    </button>
    <button class="action-btn">
      <Icon name="copy" size={16} />
      Copy Command
    </button>
  </div>
</div>

<style>
  .workflow-result {
    background: var(--bg-secondary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    overflow: hidden;
  }

  .result-header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-4);
    padding: var(--space-6);
    border-bottom: 1px solid var(--border-subtle);
  }

  .result-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1));
    border-radius: var(--radius-md);
    color: var(--accent-secondary);
    flex-shrink: 0;
  }

  .result-title {
    flex: 1;
    min-width: 0;
  }

  .result-title h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .result-desc {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-top: var(--space-1);
    display: block;
  }

  .source-badge {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: var(--radius-full);
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    flex-shrink: 0;
  }

  .source-badge.official {
    background: rgba(34, 197, 94, 0.1);
    color: var(--accent-success);
  }

  .source-badge.protocol {
    background: rgba(59, 130, 246, 0.1);
    color: var(--accent-primary);
  }

  .workflow-steps {
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .step {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    border-radius: var(--radius-md);
    background: var(--bg-tertiary);
    opacity: 0.6;
    transition: all var(--transition-fast);
  }

  .step.first {
    opacity: 1;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.05));
    border: 1px solid rgba(59, 130, 246, 0.2);
  }

  .step-number {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-secondary);
    border-radius: var(--radius-full);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .step.first .step-number {
    background: var(--accent-primary);
    color: white;
  }

  .step-content {
    flex: 1;
    min-width: 0;
  }

  .step-name {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
  }

  .step-desc {
    display: block;
    font-size: 0.75rem;
    color: var(--text-tertiary);
    margin-top: 2px;
  }

  .step-active {
    color: var(--accent-primary);
  }

  .workflow-ui {
    padding: var(--space-6);
    border-top: 1px solid var(--border-subtle);
    border-bottom: 1px solid var(--border-subtle);
  }

  .ui-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-12);
    background: var(--bg-tertiary);
    border: 2px dashed var(--border-default);
    border-radius: var(--radius-lg);
    text-align: center;
    color: var(--text-tertiary);
  }

  .ui-placeholder p {
    font-size: 0.9375rem;
    color: var(--text-secondary);
    margin-top: var(--space-4);
  }

  .ui-placeholder span {
    font-size: 0.8125rem;
    margin-top: var(--space-1);
  }

  .actions {
    display: flex;
    gap: var(--space-3);
    padding: var(--space-5);
  }

  .action-btn {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-subtle);
    transition: all var(--transition-fast);
  }

  .action-btn:hover {
    border-color: var(--border-default);
    color: var(--text-primary);
  }

  .action-btn.primary {
    background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
    border: none;
    color: white;
  }

  .action-btn.primary:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-glow);
  }
</style>
